import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw } from 'lucide-react';

const MoodPendulum = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  
  // Double pendulum physics
  const pendulumRef = useRef({
    angle1: Math.PI / 2,
    angle2: Math.PI / 2,
    velocity1: 0,
    velocity2: 0,
    length1: 150,
    length2: 150,
    mass1: 10,
    mass2: 10,
    gravity: 1,
    damping: 0.999,
    trail: []
  });

  // One-time setup: size the canvas and wire up the resize listener.
  // This must not re-run on pause/resume, or the canvas bitmap gets
  // cleared (assigning canvas.width/height always wipes it) and the
  // trail/pendulum disappear while "paused" instead of freezing in place.
  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Start/stop the animation loop in response to pause state, without
  // touching the canvas bitmap, so the last drawn frame stays visible
  // while paused and resume continues from the same trail.
  useEffect(() => {
    if (!isPaused) {
      animate();
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPaused]);

  const animate = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const p = pendulumRef.current;

    // Clear with fade effect
    ctx.fillStyle = 'rgba(253, 251, 247, 0.1)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 3;

    // Calculate physics
    const num1 = -p.gravity * (2 * p.mass1 + p.mass2) * Math.sin(p.angle1);
    const num2 = -p.mass2 * p.gravity * Math.sin(p.angle1 - 2 * p.angle2);
    const num3 = -2 * Math.sin(p.angle1 - p.angle2) * p.mass2;
    const num4 = p.velocity2 * p.velocity2 * p.length2 + p.velocity1 * p.velocity1 * p.length1 * Math.cos(p.angle1 - p.angle2);
    const den = p.length1 * (2 * p.mass1 + p.mass2 - p.mass2 * Math.cos(2 * p.angle1 - 2 * p.angle2));
    const accel1 = (num1 + num2 + num3 * num4) / den;

    const num5 = 2 * Math.sin(p.angle1 - p.angle2);
    const num6 = p.velocity1 * p.velocity1 * p.length1 * (p.mass1 + p.mass2);
    const num7 = p.gravity * (p.mass1 + p.mass2) * Math.cos(p.angle1);
    const num8 = p.velocity2 * p.velocity2 * p.length2 * p.mass2 * Math.cos(p.angle1 - p.angle2);
    const den2 = p.length2 * (2 * p.mass1 + p.mass2 - p.mass2 * Math.cos(2 * p.angle1 - 2 * p.angle2));
    const accel2 = (num5 * (num6 + num7 + num8)) / den2;

    p.velocity1 += accel1;
    p.velocity2 += accel2;
    p.velocity1 *= p.damping;
    p.velocity2 *= p.damping;
    p.angle1 += p.velocity1;
    p.angle2 += p.velocity2;

    // Calculate positions
    const x1 = centerX + p.length1 * Math.sin(p.angle1);
    const y1 = centerY + p.length1 * Math.cos(p.angle1);
    const x2 = x1 + p.length2 * Math.sin(p.angle2);
    const y2 = y1 + p.length2 * Math.cos(p.angle2);

    // Add to trail
    p.trail.push({ x: x2, y: y2 });
    if (p.trail.length > 500) p.trail.shift();

    // Draw trail
    for (let i = 0; i < p.trail.length - 1; i++) {
      const alpha = i / p.trail.length;
      const hue = (i * 2) % 360;
      ctx.strokeStyle = `hsla(${hue}, 70%, 60%, ${alpha})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(p.trail[i].x, p.trail[i].y);
      ctx.lineTo(p.trail[i + 1].x, p.trail[i + 1].y);
      ctx.stroke();
    }

    // Draw pendulum arms
    ctx.strokeStyle = '#2D3748';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    // Draw pivot
    ctx.fillStyle = '#2D3748';
    ctx.beginPath();
    ctx.arc(centerX, centerY, 8, 0, Math.PI * 2);
    ctx.fill();

    // Draw masses
    ctx.fillStyle = '#81B29A';
    ctx.beginPath();
    ctx.arc(x1, y1, 15, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#20B2AA';
    ctx.beginPath();
    ctx.arc(x2, y2, 15, 0, Math.PI * 2);
    ctx.fill();

    animationRef.current = requestAnimationFrame(animate);
  };

  const reset = () => {
    const p = pendulumRef.current;
    p.angle1 = Math.PI / 2 + (Math.random() - 0.5) * 0.5;
    p.angle2 = Math.PI / 2 + (Math.random() - 0.5) * 0.5;
    p.velocity1 = 0;
    p.velocity2 = 0;
    p.trail = [];
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#E0F7FA] relative overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
      />

      <div className="relative z-10 p-6">
        <header className="flex justify-between items-center mb-8">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-[#2D3748]">Rainbow Pendulum</h1>
          
          <div className="flex gap-3">
            <button
              data-testid="toggle-pause"
              onClick={togglePause}
              className="bg-[#81B29A] text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all duration-300 shadow-lg"
            >
              {isPaused ? 'Resume' : 'Pause'}
            </button>
            <button
              data-testid="reset-pendulum"
              onClick={reset}
              className="bg-white border-2 border-[#81B29A] text-[#81B29A] px-6 py-3 rounded-full font-semibold hover:bg-[#81B29A]/10 transition-all duration-300 flex items-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
              Reset
            </button>
          </div>
        </header>

        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl p-6 shadow-2xl border border-slate-100 max-w-md">
          <p className="text-[#4A5568] text-center">
            Watch the chaotic beauty of a double pendulum. Each swing is unique and unpredictable, creating mesmerizing rainbow trails.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MoodPendulum;